import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, TrendingUp, Users, CheckCircle2, PlayCircle } from "lucide-react";
import GlowOrbs from "@/components/common/GlowOrbs";

const dashCards = [
  { title: "Active submissions", value: "24", trend: "+8", accent: "#10B981" },
  { title: "Interview loops", value: "12", trend: "+4", accent: "#3B82F6" },
  { title: "Offers pending", value: "3", trend: "+1", accent: "#F59E0B" },
];

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-16 sm:pt-24 pb-20 sm:pb-32 noise" data-testid="hero-section">
      <GlowOrbs />
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5"
            >
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              <span className="font-inter text-[11px] uppercase tracking-[0.2em] text-white/70">AI Career Engine • US IT Placements</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-poppins text-[42px] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white font-semibold"
            >
              Launch your
              <br />
              <span className="text-gradient">US tech career.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="font-inter text-lg text-white/60 max-w-xl leading-relaxed"
            >
              SKVY Harmony Placement is the trusted staffing partner for <span className="text-white">international students, OPT, CPT and H-1B professionals</span> across Software, Data, AI, Cloud, DevOps and Cybersecurity. We match your skills to visa-friendly US employers — and stay with you from resume to offer to onboarding.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-wrap items-center gap-3"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-hire"
                className="inline-flex items-center gap-2 text-white font-inter font-medium text-sm px-6 py-3.5 rounded-full transition-transform hover:scale-[1.03]"
                style={{ background: "linear-gradient(120deg, #1E3A8A, #10B981)", boxShadow: "0 20px 40px -12px rgba(16,185,129,0.5)" }}
              >
                Upload Your Resume <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-consult"
                className="inline-flex items-center gap-2 text-white/85 font-inter text-sm px-6 py-3.5 rounded-full border border-white/10 hover:bg-white/5"
              >
                <PlayCircle className="w-4 h-4" /> Book Free Consultation
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="flex flex-wrap gap-x-6 gap-y-2 pt-4"
            >
              {["1,000+ students placed", "1,200+ US employer partners", "OPT / CPT / H-1B friendly"].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                  <span className="font-inter text-xs text-white/60">{t}</span>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative"
            >
              {/* Main dashboard card */}
              <div className="glass rounded-3xl p-6 sm:p-7 shine-border">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #1E3A8A, #10B981)" }}>
                      <Sparkles className="w-4.5 h-4.5 text-white" strokeWidth={2.3} />
                    </div>
                    <div>
                      <p className="font-poppins text-white font-semibold text-sm">SKVY Career Console</p>
                      <p className="font-inter text-[10px] uppercase tracking-[0.18em] text-white/40">Live • today</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    {["#EF4444", "#F59E0B", "#10B981"].map((c) => (
                      <span key={c} className="w-2 h-2 rounded-full" style={{ background: c }} />
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  {dashCards.map((c) => (
                    <div key={c.title} className="rounded-2xl p-3.5 bg-white/[0.03] border border-white/5">
                      <p className="font-inter text-[10px] uppercase tracking-[0.14em] text-white/40">{c.title}</p>
                      <p className="font-poppins text-2xl text-white font-semibold mt-1.5">{c.value}</p>
                      <p className="font-inter text-[11px] mt-0.5" style={{ color: c.accent }}>{c.trend} this week</p>
                    </div>
                  ))}
                </div>

                {/* Pipeline row */}
                <div className="rounded-2xl bg-white/[0.03] border border-white/5 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-inter text-[11px] uppercase tracking-[0.18em] text-white/50">Pipeline health</p>
                    <p className="font-inter text-[11px] text-[#10B981]">Healthy</p>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { l: "Sourced", w: "92%", c: "#3B82F6" },
                      { l: "Screened", w: "68%", c: "#8B5CF6" },
                      { l: "Interviewed", w: "42%", c: "#10B981" },
                      { l: "Offered", w: "18%", c: "#F59E0B" },
                    ].map((r) => (
                      <div key={r.l} className="grid grid-cols-[80px_1fr_40px] items-center gap-3">
                        <span className="font-inter text-[11px] text-white/60">{r.l}</span>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: r.w }}
                            transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
                            className="h-full rounded-full"
                            style={{ background: r.c }}
                          />
                        </div>
                        <span className="font-inter text-[11px] text-white/50 text-right">{r.w}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating candidate card */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-4 sm:-left-10 -bottom-6 sm:-bottom-10 glass rounded-2xl p-4 w-[240px] hidden sm:block"
                style={{ boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#10B981] flex items-center justify-center text-white font-poppins font-semibold">
                    AK
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-poppins text-white text-sm font-medium truncate">Aarav Kulkarni</p>
                    <p className="font-inter text-[10px] text-white/50 truncate">SDE • OPT • 96% fit</p>
                  </div>
                </div>
                <div className="mt-3 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full w-[96%] rounded-full" style={{ background: "linear-gradient(90deg, #10B981, #3B82F6)" }} />
                </div>
              </motion.div>

              {/* Floating stat chip */}
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-2 sm:-right-6 -top-6 glass rounded-2xl p-3.5 flex items-center gap-3"
              >
                <span className="w-9 h-9 rounded-xl bg-[#10B981]/20 border border-[#10B981]/30 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-[#10B981]" />
                </span>
                <div>
                  <p className="font-poppins text-white text-sm font-semibold leading-none">1,000+</p>
                  <p className="font-inter text-[10px] text-white/50 mt-0.5">students placed</p>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute right-6 top-1/2 -translate-y-1/2 glass rounded-2xl px-3.5 py-2.5 flex items-center gap-2 hidden md:flex"
              >
                <Users className="w-3.5 h-3.5 text-[#3B82F6]" />
                <span className="font-inter text-[11px] text-white/80">40,000+ live US requisitions</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
